import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  FloatingLabel,
  Alert,
  Row,
  Col,
  Card,
  ProgressBar,
  Badge,
} from "react-bootstrap";
import {
  IoClose,
  IoCloudUpload,
  IoTrash,
  IoCalendar,
  IoCash,
  IoBusiness,
  IoLocation,
  IoBag,
  IoStar,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoMedical,
} from "react-icons/io5";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Quill from "quill";
import "quill/dist/quill.snow.css";

export const UnitType = {
  MEDIS: "Medis",
  KEPERAWATAN: "Keperawatan",
  ADMINISTRASI: "Administrasi",
  PENUNJANG_MEDIS: "Penunjang Medis",
  MANAJEMEN: "Manajemen",
  PENDIDIKAN: "Pendidikan",
  TENAGA_PENDUKUNG: "Tenaga Pendukung",
  PARAMEDIS: "Paramedis",
};

const CareerModal = ({ show, handleClose, onSubmit, initialData }) => {
  const editorRef = useRef(null);
  const quillInstanceRef = useRef(null);
  const [isEditorInitialized, setIsEditorInitialized] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    specialization: "",
    category: "",
    location: "",
    employment_type: "Full Time",
    description: "",
    requirements: "",
    required_skills: [],
    benefits: "",
    salary_min: "",
    salary_max: "",
    application_deadline: "",
    position_level: "Senior",
    is_urgent: false,
    available_positions: 1,
    current_applicants: 0,
    experience_required: 0,
    remote_allowed: false,
    work_environment: "General",
    is_active: true,
    thumbnail: "",
    thumbnailFile: null,
    thumbnailBase64: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [debugInfo, setDebugInfo] = useState({});

  const fileInputRef = useRef(null);
  const modalBodyRef = useRef(null);

  useEffect(() => {
    if (show && editorRef.current && !quillInstanceRef.current) {
      while (editorRef.current.firstChild) {
        editorRef.current.removeChild(editorRef.current.firstChild);
      }

      const editorContainer = document.createElement("div");
      editorContainer.style.height = "200px";
      editorContainer.style.border = "1px solid #ccc";
      editorContainer.style.borderRadius = "4px";
      editorRef.current.appendChild(editorContainer);

      quillInstanceRef.current = new Quill(editorContainer, {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline"],
            ["blockquote", "code-block"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ align: [] }],
            ["link", "image"],
            ["clean"],
          ],
        },
        theme: "snow",
        placeholder: "Deskripsikan tanggung jawab dan ekspektasi pekerjaan...",
      });

      if (formData.description) {
        quillInstanceRef.current.root.innerHTML = formData.description;
      }

      quillInstanceRef.current.on("text-change", () => {
        const content = quillInstanceRef.current.root.innerHTML;
        setFormData((prev) => ({
          ...prev,
          description: content,
        }));
      });

      setIsEditorInitialized(true);
    }

    return () => {
      if (quillInstanceRef.current) {
        quillInstanceRef.current = null;
        setIsEditorInitialized(false);
      }
    };
  }, [show]);

  useEffect(() => {
    if (
      isEditorInitialized &&
      quillInstanceRef.current &&
      formData.description
    ) {
      const currentContent = quillInstanceRef.current.root.innerHTML;
      if (currentContent !== formData.description) {
        quillInstanceRef.current.root.innerHTML = formData.description;
      }
    }
  }, [formData.description, isEditorInitialized]);

  useEffect(() => {
    if (show) {
      if (initialData) {
        const skillsForSelect =
          initialData.required_skills?.map((skill) => ({
            value: skill,
            label: skill,
          })) || [];

        const newFormData = {
          title: initialData.title || "",
          specialization: initialData.specialization || "",
          category: initialData.category || "",
          location: initialData.location || "",
          employment_type: initialData.employment_type || "Full Time",
          description: initialData.description || "",
          requirements: initialData.requirements || "",
          required_skills: skillsForSelect,
          benefits: initialData.benefits || "",
          salary_min: initialData.salary_min?.toString() || "",
          salary_max: initialData.salary_max?.toString() || "",
          application_deadline: initialData.application_deadline
            ? new Date(initialData.application_deadline)
                .toISOString()
                .split("T")[0]
            : "",
          position_level: initialData.position_level || "Senior",
          is_urgent: initialData.is_urgent || false,
          available_positions: initialData.available_positions || 1,
          current_applicants: initialData.current_applicants || 0,
          experience_required: initialData.experience_required || 0,
          remote_allowed: initialData.remote_allowed || false,
          work_environment: initialData.work_environment || "General",
          is_active: initialData.is_active ?? true,
          thumbnail: initialData.thumbnail || "",
          thumbnailFile: null,
          thumbnailBase64: "",
        };

        setFormData(newFormData);

        setTimeout(() => {
          if (quillInstanceRef.current && newFormData.description) {
            quillInstanceRef.current.root.innerHTML = newFormData.description;
          }
        }, 100);
      } else {
        const defaultFormData = {
          title: "",
          specialization: "",
          category: "",
          location: "",
          employment_type: "Full Time",
          description: "",
          requirements: "",
          required_skills: [],
          benefits: "",
          salary_min: "",
          salary_max: "",
          application_deadline: "",
          position_level: "Senior",
          is_urgent: false,
          available_positions: 1,
          current_applicants: 0,
          experience_required: 0,
          remote_allowed: false,
          work_environment: "General",
          is_active: true,
          thumbnail: "",
          thumbnailFile: null,
          thumbnailBase64: "",
        };

        setFormData(defaultFormData);

        setTimeout(() => {
          if (quillInstanceRef.current) {
            quillInstanceRef.current.root.innerHTML = "";
          }
        }, 100);
      }

      setErrors({});
      setIsUploading(false);
      setUploadProgress(0);

      if (modalBodyRef.current) {
        modalBodyRef.current.scrollTop = 0;
      }
    }
  }, [show, initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSkillsChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      required_skills: selectedOptions || [],
    }));

    if (errors.required_skills) {
      setErrors((prev) => ({ ...prev, required_skills: null }));
    }
  };

  const handleCreateSkill = (inputValue) => {
    const newSkill = {
      value: inputValue,
      label: inputValue,
    };
    setFormData((prev) => ({
      ...prev,
      required_skills: [...prev.required_skills, newSkill],
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: "File harus berupa gambar (JPEG, PNG, GIF, WebP)",
      }));
      return;
    }

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        thumbnail: "Ukuran file maksimal 5MB",
      }));
      return;
    }

    if (errors.thumbnail) {
      setErrors((prev) => ({ ...prev, thumbnail: null }));
    }

    setFormData((prev) => ({
      ...prev,
      thumbnailFile: file,
      thumbnail: URL.createObjectURL(file),
    }));

    convertToBase64(file);
  };

  const convertToBase64 = (file) => {
    setIsUploading(true);
    setUploadProgress(30);

    const reader = new FileReader();

    reader.onloadstart = () => {
      setUploadProgress(50);
    };

    reader.onload = (e) => {
      const base64String = e.target.result;
      setFormData((prev) => ({
        ...prev,
        thumbnailBase64: base64String,
      }));
      setUploadProgress(100);

      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    };

    reader.onerror = (error) => {
      console.error("Error converting file:", error);
      setErrors((prev) => ({
        ...prev,
        thumbnail: "Gagal mengkonversi gambar",
      }));
      setIsUploading(false);
      setUploadProgress(0);
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveThumbnail = () => {
    if (formData.thumbnail && formData.thumbnail.startsWith("blob:")) {
      URL.revokeObjectURL(formData.thumbnail);
    }

    setFormData((prev) => ({
      ...prev,
      thumbnail: "",
      thumbnailFile: null,
      thumbnailBase64: "",
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Judul lowongan wajib diisi";
    }

    if (!formData.specialization) {
      newErrors.specialization = "Spesialisasi wajib dipilih";
    }

    if (!formData.category) {
      newErrors.category = "Kategori wajib dipilih";
    }

    if (!formData.location.trim()) {
      newErrors.location = "Lokasi wajib diisi";
    }

    const textContent = formData.description
      ? formData.description.replace(/<[^>]*>/g, "").trim()
      : "";

    if (!textContent) {
      newErrors.description = "Deskripsi wajib diisi";
    }

    if (!formData.requirements.trim()) {
      newErrors.requirements = "Persyaratan wajib diisi";
    }

    if (!formData.application_deadline) {
      newErrors.application_deadline = "Deadline aplikasi wajib diisi";
    } else {
      const deadline = new Date(formData.application_deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (deadline <= today) {
        newErrors.application_deadline = "Deadline harus lebih dari hari ini";
      }
    }

    if (formData.salary_min && formData.salary_max) {
      const min = parseFloat(formData.salary_min);
      const max = parseFloat(formData.salary_max);
      if (min > max) {
        newErrors.salary_min =
          "Gaji minimum tidak boleh lebih besar dari maksimum";
      }
    }

    if (formData.available_positions < 1) {
      newErrors.available_positions = "Jumlah posisi minimal 1";
    }

    if (formData.experience_required < 0) {
      newErrors.experience_required = "Pengalaman tidak boleh negatif";
    }

    setErrors(newErrors);

    setDebugInfo({
      validationErrors: Object.keys(newErrors).length,
      requiredFields: {
        title: !!formData.title.trim(),
        specialization: !!formData.specialization,
        category: !!formData.category,
        location: !!formData.location.trim(),
        description: !!textContent,
        requirements: !!formData.requirements.trim(),
        deadline: !!formData.application_deadline,
      },
      formDataSnapshot: {
        title:
          formData.title.substring(0, 20) +
          (formData.title.length > 20 ? "..." : ""),
        specialization: formData.specialization,
        category: formData.category,
        deadline: formData.application_deadline,
      },
    });

    return Object.keys(newErrors).length === 0;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isValid = validateForm();

    if (!isValid) {
      setTimeout(() => {
        const firstErrorElement = document.querySelector(".is-invalid");
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstErrorElement.focus();
        }
      }, 100);

      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        title: formData.title.trim(),
        specialization: formData.specialization,
        category: formData.category,
        location: formData.location.trim(),
        employment_type: formData.employment_type,
        description: formData.description,
        requirements: formData.requirements.trim(),
        required_skills: formData.required_skills.map((skill) => skill.value),
        benefits: formData.benefits?.trim() || "",
        salary_min: formData.salary_min
          ? parseFloat(formData.salary_min)
          : null,
        salary_max: formData.salary_max
          ? parseFloat(formData.salary_max)
          : null,
        application_deadline: formData.application_deadline,
        position_level: formData.position_level,
        is_urgent: Boolean(formData.is_urgent),
        available_positions: parseInt(formData.available_positions) || 1,
        current_applicants: parseInt(formData.current_applicants) || 0,
        experience_required: parseInt(formData.experience_required) || 0,
        remote_allowed: Boolean(formData.remote_allowed),
        work_environment: formData.work_environment,
        is_active: Boolean(formData.is_active),
        thumbnail: formData.thumbnailBase64 || formData.thumbnail || null,
      };

      if (!submitData.benefits) delete submitData.benefits;
      if (!submitData.salary_min) delete submitData.salary_min;
      if (!submitData.salary_max) delete submitData.salary_max;
      if (!submitData.thumbnail) delete submitData.thumbnail;

      if (!onSubmit || typeof onSubmit !== "function") {
        const errorMsg =
          "Error: Submit handler tidak tersedia atau bukan fungsi";
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error("❌ FORM SUBMISSION ERROR:", error);
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "Gagal menyimpan data. Silakan coba lagi.",
      }));

      setTimeout(() => {
        modalBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateDaysRemaining = () => {
    if (!formData.application_deadline) return null;
    const deadline = new Date(formData.application_deadline);
    const today = new Date();
    const timeDiff = deadline.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const daysRemaining = calculateDaysRemaining();

  // CATEGORY OPTIONS dari enum UnitType
  const categoryOptions = [
    { value: UnitType.MEDIS, label: UnitType.MEDIS },
    { value: UnitType.KEPERAWATAN, label: UnitType.KEPERAWATAN },
    { value: UnitType.ADMINISTRASI, label: UnitType.ADMINISTRASI },
    { value: UnitType.PENUNJANG_MEDIS, label: UnitType.PENUNJANG_MEDIS },
    { value: UnitType.MANAJEMEN, label: UnitType.MANAJEMEN },
    { value: UnitType.PENDIDIKAN, label: UnitType.PENDIDIKAN },
    { value: UnitType.TENAGA_PENDUKUNG, label: UnitType.TENAGA_PENDUKUNG },
    { value: UnitType.PARAMEDIS, label: UnitType.PARAMEDIS },
  ];

  const specializationOptions = [
    { value: "Cardiology", label: "Cardiology" },
    { value: "Internal Medicine", label: "Internal Medicine" },
    { value: "Surgery", label: "Surgery" },
    { value: "Pediatrics", label: "Pediatrics" },
    { value: "Obstetrics & Gynecology", label: "Obstetrics & Gynecology" },
    { value: "Neurology", label: "Neurology" },
    { value: "Radiology", label: "Radiology" },
    { value: "Anesthesiology", label: "Anesthesiology" },
    { value: "Psychiatry", label: "Psychiatry" },
    { value: "Dermatology", label: "Dermatology" },
    { value: "Orthopedics", label: "Orthopedics" },
    { value: "Urology", label: "Urology" },
    { value: "Ophthalmology", label: "Ophthalmology" },
    { value: "ENT", label: "ENT" },
    { value: "General Practitioner", label: "General Practitioner" },
  ];

  const employmentTypeOptions = [
    { value: "Full Time", label: "Full Time" },
    { value: "Part Time", label: "Part Time" },
    { value: "Contract", label: "Contract" },
    { value: "Internship", label: "Internship" },
    { value: "Freelance", label: "Freelance" },
  ];

  const positionLevelOptions = [
    { value: "Intern", label: "Intern" },
    { value: "Junior", label: "Junior" },
    { value: "Mid Level", label: "Mid Level" },
    { value: "Senior", label: "Senior" },
    { value: "Senior Specialist", label: "Senior Specialist" },
    { value: "Lead", label: "Lead" },
    { value: "Manager", label: "Manager" },
    { value: "Director", label: "Director" },
  ];

  const workEnvironmentOptions = [
    { value: "General", label: "General" },
    { value: "Islami", label: "Islami" },
    { value: "Multicultural", label: "Multicultural" },
    { value: "International", label: "International" },
    { value: "Remote Friendly", label: "Remote Friendly" },
  ];

  const suggestedSkills = [
    { value: "Kardiologi Intervensi", label: "Kardiologi Intervensi" },
    { value: "Echocardiography", label: "Echocardiography" },
    { value: "Manajemen ICU", label: "Manajemen ICU" },
    { value: "Penelitian Klinis", label: "Penelitian Klinis" },
    { value: "Bedah", label: "Bedah" },
    { value: "Diagnosis", label: "Diagnosis" },
    { value: "Perawatan Pasien", label: "Perawatan Pasien" },
    { value: "Emergency Medicine", label: "Emergency Medicine" },
    { value: "Pediatric Care", label: "Pediatric Care" },
    { value: "Radiologi", label: "Radiologi" },
    { value: "Anestesi", label: "Anestesi" },
    { value: "Laboratorium", label: "Laboratorium" },
    { value: "Farmasi", label: "Farmasi" },
    { value: "Keperawatan", label: "Keperawatan" },
    { value: "Manajemen Kesehatan", label: "Manajemen Kesehatan" },
  ];

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="career-modal"
      onExited={() => {
        if (quillInstanceRef.current) {
          quillInstanceRef.current = null;
          setIsEditorInitialized(false);
        }
      }}
    >
      <Modal.Header
        closeButton
        className="bg-light sticky-top"
        style={{
          borderBottom: "1px solid #dee2e6",
          zIndex: 1050,
        }}
      >
        <Modal.Title className="fw-bold">
          {initialData ? (
            <>
              <IoBusiness className="me-2" />
              Edit Lowongan Pekerjaan
            </>
          ) : (
            <>
              <IoBag className="me-2" />
              Tambah Lowongan Baru
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleFormSubmit} noValidate>
        {errors.submit && (
          <div className="mx-3 mt-3">
            <Alert
              variant="danger"
              dismissible
              onClose={() => setErrors((prev) => ({ ...prev, submit: null }))}
              className="mb-0"
            >
              <Alert.Heading>
                <IoAlertCircle className="me-2" />
                Error!
              </Alert.Heading>
              <p className="mb-0">{errors.submit}</p>
            </Alert>
          </div>
        )}

        <div
          ref={modalBodyRef}
          style={{
            maxHeight: "calc(85vh - 150px)",
            overflowY: "auto",
            padding: "20px",
          }}
          className="modal-body-scrollable"
        >
          <div className="row g-3">
            {process.env.NODE_ENV === "development" && (
              <div className="col-12">
                <Card className="mb-3 border-warning">
                  <Card.Header className="bg-warning bg-opacity-10 py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="fw-bold text-warning">Debug Info</small>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => {
                          console.log("=== DEBUG INFO ===");
                          console.log("Form Data:", formData);
                          console.log("Errors:", errors);
                          console.log("Is Submitting:", isSubmitting);
                          console.log("Is Uploading:", isUploading);
                          console.log(
                            "Quill initialized:",
                            isEditorInitialized,
                          );
                          console.log("onSubmit function:", onSubmit);
                          console.log("=== END DEBUG ===");
                        }}
                      >
                        Log to Console
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <div className="row small">
                      <div className="col-6">
                        <div>
                          <strong>Validation:</strong>{" "}
                          {debugInfo.validationErrors || 0} errors
                        </div>
                        <div>
                          <strong>Required Fields:</strong>
                        </div>
                        <ul className="small mb-1">
                          <li
                            className={
                              debugInfo.requiredFields?.title
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            Title: {debugInfo.requiredFields?.title ? "✓" : "✗"}
                          </li>
                          <li
                            className={
                              debugInfo.requiredFields?.specialization
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            Specialization:{" "}
                            {debugInfo.requiredFields?.specialization
                              ? "✓"
                              : "✗"}
                          </li>
                          <li
                            className={
                              debugInfo.requiredFields?.category
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            Category:{" "}
                            {debugInfo.requiredFields?.category ? "✓" : "✗"}
                          </li>
                          <li
                            className={
                              debugInfo.requiredFields?.location
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            Location:{" "}
                            {debugInfo.requiredFields?.location ? "✓" : "✗"}
                          </li>
                          <li
                            className={
                              debugInfo.requiredFields?.description
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            Description:{" "}
                            {debugInfo.requiredFields?.description ? "✓" : "✗"}
                          </li>
                          <li
                            className={
                              debugInfo.requiredFields?.requirements
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            Requirements:{" "}
                            {debugInfo.requiredFields?.requirements ? "✓" : "✗"}
                          </li>
                          <li
                            className={
                              debugInfo.requiredFields?.deadline
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            Deadline:{" "}
                            {debugInfo.requiredFields?.deadline ? "✓" : "✗"}
                          </li>
                        </ul>
                      </div>
                      <div className="col-6">
                        <div>
                          <strong>Status:</strong>
                        </div>
                        <div>Submitting: {isSubmitting ? "Yes" : "No"}</div>
                        <div>Uploading: {isUploading ? "Yes" : "No"}</div>
                        <div>
                          Quill Initialized:{" "}
                          {isEditorInitialized ? "Yes" : "No"}
                        </div>
                        <div>
                          Button Disabled:{" "}
                          {isSubmitting || isUploading ? "Yes" : "No"}
                        </div>
                        <div>
                          onSubmit:{" "}
                          {typeof onSubmit === "function"
                            ? "✓ Function"
                            : "✗ Not Function"}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}

            <div className="col-12 mb-4">
              <h5 className="fw-bold border-bottom pb-2 mb-3">
                <IoBusiness className="me-2" />
                Informasi Dasar
              </h5>

              <div className="row g-1">
                {/* Judul Lowongan - FULL WIDTH */}
                <div className="col-12">
                  <FloatingLabel
                    controlId="title"
                    label="Judul Lowongan *"
                    className="mb-0"
                  >
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Contoh: Dokter Spesialis Jantung"
                      isInvalid={!!errors.title}
                      disabled={isSubmitting}
                      className="px-3"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>

                {/* Spesialisasi */}
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="specialization"
                    label="Spesialisasi *"
                    className="mb-0"
                  >
                    <Form.Select
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      isInvalid={!!errors.specialization}
                      disabled={isSubmitting}
                      className="px-3"
                    >
                      <option value="">Pilih spesialisasi...</option>
                      {specializationOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.specialization}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>

                {/* Lokasi */}
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="location"
                    label="Lokasi *"
                    className="mb-0"
                  >
                    <Form.Control
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Contoh: Jakarta Pusat"
                      isInvalid={!!errors.location}
                      disabled={isSubmitting}
                      className="px-3"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.location}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>

                {/* Jenis Pekerjaan */}
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="employment_type"
                    label="Jenis Pekerjaan"
                    className="mb-0"
                  >
                    <Form.Select
                      name="employment_type"
                      value={formData.employment_type}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="px-3"
                    >
                      {employmentTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </div>

                {/* Level Posisi */}
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="position_level"
                    label="Level Posisi"
                    className="mb-0"
                  >
                    <Form.Select
                      name="position_level"
                      value={formData.position_level}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="px-3"
                    >
                      {positionLevelOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </div>

                {/* Lingkungan Kerja */}
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="work_environment"
                    label="Lingkungan Kerja"
                    className="mb-0"
                  >
                    <Form.Select
                      name="work_environment"
                      value={formData.work_environment}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="px-3"
                    >
                      {workEnvironmentOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </div>

                {/* Kategori - DIPINDAHKAN KE BAWAH */}
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="category"
                    label="Kategori *"
                    className="mb-0"
                  >
                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      isInvalid={!!errors.category}
                      disabled={isSubmitting}
                      className="px-3"
                    >
                      <option value="">Pilih kategori...</option>
                      {categoryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.category}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>
              </div>
            </div>

            <div className="col-12 mb-4">
              <h5 className="fw-bold border-bottom pb-2 mb-3">
                <IoCash className="me-2" />
                Informasi Gaji
              </h5>

              <div className="row g-3">
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="salary_min"
                    label="Gaji Minimum (Rp)"
                  >
                    <Form.Control
                      type="number"
                      name="salary_min"
                      value={formData.salary_min}
                      onChange={handleChange}
                      placeholder="e.g., 45000000"
                      isInvalid={!!errors.salary_min}
                      min="0"
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.salary_min}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>

                <div className="col-md-6">
                  <FloatingLabel
                    controlId="salary_max"
                    label="Gaji Maksimum (Rp)"
                  >
                    <Form.Control
                      type="number"
                      name="salary_max"
                      value={formData.salary_max}
                      onChange={handleChange}
                      placeholder="e.g., 70000000"
                      min="0"
                      disabled={isSubmitting}
                    />
                  </FloatingLabel>
                </div>
              </div>
            </div>

            <div className="col-12 mb-4">
              <h5 className="fw-bold border-bottom pb-2 mb-3">
                <IoCheckmarkCircle className="me-2" />
                Detail Pekerjaan
              </h5>

              <div className="row g-3">
                <div className="col-12">
                  <Form.Label className="fw-semibold">
                    Deskripsi Pekerjaan *
                  </Form.Label>
                  <div ref={editorRef}></div>
                  {errors.description && (
                    <div className="invalid-feedback d-block">
                      {errors.description}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <FloatingLabel
                    controlId="requirements"
                    label="Persyaratan & Kualifikasi *"
                  >
                    <Form.Control
                      as="textarea"
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleChange}
                      placeholder="Sebutkan persyaratan dan kualifikasi yang diperlukan (satu per baris)..."
                      style={{ height: "150px" }}
                      isInvalid={!!errors.requirements}
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.requirements}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Gunakan baris baru atau bullet points untuk setiap
                      persyaratan
                    </Form.Text>
                  </FloatingLabel>
                </div>

                <div className="col-12">
                  <Form.Label className="fw-semibold">
                    Keahlian yang Dibutuhkan
                  </Form.Label>
                  <CreatableSelect
                    isMulti
                    options={suggestedSkills}
                    value={formData.required_skills}
                    onChange={handleSkillsChange}
                    onCreateOption={handleCreateSkill}
                    placeholder="Pilih atau tambah keahlian..."
                    noOptionsMessage={() =>
                      "Ketik untuk menambah keahlian baru"
                    }
                    formatCreateLabel={(inputValue) => `Tambah "${inputValue}"`}
                    isDisabled={isSubmitting}
                  />
                </div>

                <div className="col-12">
                  <FloatingLabel
                    controlId="benefits"
                    label="Benefit & Fasilitas"
                  >
                    <Form.Control
                      as="textarea"
                      name="benefits"
                      value={formData.benefits}
                      onChange={handleChange}
                      placeholder="Sebutkan benefit dan fasilitas yang ditawarkan..."
                      style={{ height: "100px" }}
                      disabled={isSubmitting}
                    />
                  </FloatingLabel>
                </div>
              </div>
            </div>

            <div className="col-12 mb-4">
              <h5 className="fw-bold border-bottom pb-2 mb-3">
                <IoCalendar className="me-2" />
                Detail Aplikasi
              </h5>

              <div className="row g-3">
                <div className="col-md-6">
                  <FloatingLabel
                    controlId="application_deadline"
                    label="Deadline Aplikasi *"
                  >
                    <Form.Control
                      type="date"
                      name="application_deadline"
                      value={formData.application_deadline}
                      onChange={handleChange}
                      isInvalid={!!errors.application_deadline}
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.application_deadline}
                    </Form.Control.Feedback>
                    {daysRemaining !== null && (
                      <Form.Text
                        className={
                          daysRemaining <= 7 ? "text-warning" : "text-success"
                        }
                      >
                        {daysRemaining > 0
                          ? `${daysRemaining} hari tersisa`
                          : "Deadline sudah lewat"}
                      </Form.Text>
                    )}
                  </FloatingLabel>
                </div>

                <div className="col-md-3">
                  <FloatingLabel
                    controlId="available_positions"
                    label="Jumlah Posisi"
                  >
                    <Form.Control
                      type="number"
                      name="available_positions"
                      value={formData.available_positions}
                      onChange={handleChange}
                      min="1"
                      isInvalid={!!errors.available_positions}
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.available_positions}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>

                <div className="col-md-3">
                  <FloatingLabel
                    controlId="experience_required"
                    label="Pengalaman (tahun)"
                  >
                    <Form.Control
                      type="number"
                      name="experience_required"
                      value={formData.experience_required}
                      onChange={handleChange}
                      min="0"
                      isInvalid={!!errors.experience_required}
                      disabled={isSubmitting}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.experience_required}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>
              </div>
            </div>

            <div className="col-12 mb-4">
              <h5 className="fw-bold border-bottom pb-2 mb-3">
                <IoCloudUpload className="me-2" />
                Gambar Lowongan (Opsional)
              </h5>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                className="d-none"
                disabled={isSubmitting}
              />

              {!formData.thumbnail ? (
                <Card
                  className="border-dashed text-center cursor-pointer"
                  onClick={handleUploadClick}
                  style={{
                    border: "2px dashed #dee2e6",
                    background: "#f8f9fa",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    minHeight: "150px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: isSubmitting ? 0.6 : 1,
                  }}
                  disabled={isSubmitting}
                >
                  <Card.Body className="py-4">
                    <IoCloudUpload size={36} className="text-muted mb-2" />
                    <p className="mb-1">Klik untuk upload gambar</p>
                    <p className="text-muted small mb-0">
                      Format: JPG, PNG, GIF, WebP (max 5MB)
                    </p>
                  </Card.Body>
                </Card>
              ) : (
                <Card>
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col xs={3}>
                        <div className="position-relative">
                          <img
                            src={formData.thumbnail}
                            alt="Thumbnail preview"
                            className="img-fluid rounded"
                            style={{
                              width: "100%",
                              height: "100px",
                              objectFit: "cover",
                            }}
                          />
                          {isUploading && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center rounded">
                              <div className="text-center">
                                <div
                                  className="spinner-border spinner-border-sm text-primary"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <small className="d-block mt-1">
                                  Processing...
                                </small>
                              </div>
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col xs={7}>
                        <div>
                          <strong className="d-block">
                            {formData.thumbnailFile?.name || "Gambar Lowongan"}
                          </strong>
                          <small className="text-muted">
                            {formData.thumbnailFile &&
                              formatFileSize(formData.thumbnailFile.size)}
                          </small>
                        </div>
                      </Col>
                      <Col xs={2} className="text-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRemoveThumbnail}
                          disabled={isUploading || isSubmitting}
                        >
                          <IoTrash />
                        </Button>
                      </Col>
                    </Row>

                    {isUploading && (
                      <div className="mt-3">
                        <ProgressBar
                          now={uploadProgress}
                          label={`${uploadProgress}%`}
                          animated
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {errors.thumbnail && (
                <Form.Control.Feedback type="invalid" className="d-block mt-2">
                  {errors.thumbnail}
                </Form.Control.Feedback>
              )}
            </div>

            <div className="col-12 mb-4">
              <h5 className="fw-bold border-bottom pb-2 mb-3">
                <IoAlertCircle className="me-2" />
                Opsi Tambahan
              </h5>

              <Row>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    id="is_urgent"
                    name="is_urgent"
                    label={
                      <>
                        <Badge bg="danger" className="me-2">
                          URGENT
                        </Badge>
                        Tandai sebagai Lowongan Urgent
                      </>
                    }
                    checked={formData.is_urgent}
                    onChange={handleChange}
                    className="mb-3"
                    disabled={isSubmitting}
                  />

                  <Form.Check
                    type="checkbox"
                    id="remote_allowed"
                    name="remote_allowed"
                    label="Remote Work Diperbolehkan"
                    checked={formData.remote_allowed}
                    onChange={handleChange}
                    className="mb-3"
                    disabled={isSubmitting}
                  />
                </Col>

                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    label={
                      <>
                        <IoStar className="me-2 text-warning" />
                        Lowongan Aktif
                      </>
                    }
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="mb-3"
                    disabled={isSubmitting}
                  />

                  {initialData && (
                    <div className="mt-3">
                      <FloatingLabel
                        controlId="current_applicants"
                        label="Jumlah Pelamar Saat Ini"
                      >
                        <Form.Control
                          type="number"
                          name="current_applicants"
                          value={formData.current_applicants}
                          onChange={handleChange}
                          min="0"
                          readOnly={!initialData}
                          disabled={isSubmitting}
                        />
                      </FloatingLabel>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </div>

        <Modal.Footer
          className="bg-light border-top sticky-bottom"
          style={{
            zIndex: 1050,
            backgroundColor: "#f8f9fa",
            padding: "15px 20px",
          }}
        >
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            disabled={isSubmitting || isUploading}
            className="d-flex align-items-center"
          >
            <IoClose className="me-2" /> Batal
          </Button>
          <Button
            variant={initialData ? "warning" : "primary"}
            type="submit"
            disabled={isSubmitting || isUploading}
            className="d-flex align-items-center position-relative"
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Menyimpan...
              </>
            ) : initialData ? (
              <>
                <IoCheckmarkCircle className="me-2" />
                Update Lowongan
              </>
            ) : (
              <>
                <IoCheckmarkCircle className="me-2" />
                Simpan Lowongan
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CareerModal;
